import React, { FC, useContext, useEffect, useRef } from "react";
import { PhotoManagerContext } from "..";
import { EventType } from "../types";
import EventsGrid from "../EventsGrid";
import EventsFilter from "../EventsFilter";
import NoFilteredEvents from "../NoFilteredEvents";
import { usePopupClickOutside } from "../../../../hooks/usePopupClickOutside";

const DEMO_PREP_POLL_INTERVAL_MS = 2000;

const filterSelectedIcon = require("../../styles/images/filter-selected.svg");

interface LibraryEventsViewProps {
  events: EventType[];
  albumLoading: number | null;
  showActionTooltip: (id: number | null, title: string, type: string) => void;
  handleEventClickWithNavigation: (event: EventType) => void;
  setActiveShareModalEventId: (id: number) => void;
  setMarkUploadCompletePopup: (id: number) => void;
  setPrivacyPopupId: (id: number) => void;
}

const LibraryEventsView: FC<LibraryEventsViewProps> = ({
  events,
  albumLoading,
  showActionTooltip,
  handleEventClickWithNavigation,
  setActiveShareModalEventId,
  setMarkUploadCompletePopup,
  setPrivacyPopupId
}) => {
  const context = useContext(PhotoManagerContext);
  const popupRef = useRef<HTMLDivElement>(null);
  usePopupClickOutside(popupRef, () => context?.closeFilterEventsPanel());

  // Poll the events list while any tile is "preparing" so the demo seeder
  // completion lands automatically. The ref keeps a stable handle to the
  // refetch fn (which is a new reference on every parent render) so the
  // effect only re-runs when `events` actually changes.
  const refetchRef = useRef<(() => void) | undefined>(undefined);
  refetchRef.current = context?.setRootEventsView;
  useEffect(() => {
    if (!events.some(e => e.demoPreparationInProgress)) return;
    const timeoutId = window.setTimeout(() => {
      refetchRef.current?.();
    }, DEMO_PREP_POLL_INTERVAL_MS);
    return () => window.clearTimeout(timeoutId);
  }, [events]);

  return (
    <div ref={popupRef}>
      <div className="events-title-container">
        <div className="ws-library-view-heading">Events</div>
        <div
          className="events-filter-icon"
          onClick={() => {
            if (context?.selectedEventFilter) {
              return context?.clearFilter();
            } else {
              return context?.showEventsFilterPanel();
            }
          }}
          id="filter-icon"
          data-toggle="tooltip"
          data-placement="bottom"
          onMouseEnter={e =>
            context?.showActionTooltip(null, "Filter events", "filter")
          }
        >
          <img
            src={filterSelectedIcon}
            className="ws-events-filter-icon"
            alt="events-filter-icon"
          />
          {context?.isFilterApplied() ? (
            <div className="active-filter-indicator" />
          ) : null}
        </div>
        {context?.showEventsFilter ? (
          <EventsFilter
            applyEventsFilter={(filter: string) => {
              context?.setEventsLoading();
              context?.applyEventsFilter(filter);
            }}
            selectedEventFilter={context?.selectedEventFilter || null}
            key="side-bar-head"
            className="ws-sidebar-head-filter"
          />
        ) : null}
      </div>
      {context?.noFilteredData ? (
        <NoFilteredEvents />
      ) : (
        <EventsGrid
          events={events}
          albumLoadingId={albumLoading}
          showActionTooltip={showActionTooltip}
          handleEventClickWithNavigation={handleEventClickWithNavigation}
          setActiveShareModalEventId={setActiveShareModalEventId}
          setMarkUploadCompletePopup={setMarkUploadCompletePopup}
          setPrivacyPopupId={setPrivacyPopupId}
          source="root-events"
        />
      )}
    </div>
  );
};

export default LibraryEventsView;
